import { ApiError } from '@/shared/api/client'
import type { TranslateFn } from './useTranslation'
import type { TranslationKey } from './types'

/**
 * Turns an unknown thrown value into a message the user should read.
 *
 * Why not just render `err.detail`: `Program.cs` puts `ex.Message` straight into
 * the Problem Details `detail`, and that prose is Portuguese, written for us and
 * not for the player. In an English UI it leaks server language mid-sentence, and
 * in either language it can expose internals. `Common/ErrorCodes.cs` already
 * defines stable snake_case codes, so the client can say it properly instead.
 *
 * An unmapped code falls back to the caller's message — deliberately, rather
 * than showing the raw detail, so a new server error never ships untranslated.
 */

const KNOWN_CODES = new Set<string>([
  'not_found',
  'forbidden',
  'validation_failed',
  'internal_error',
  'rate_limit_exceeded',
  'invalid_invite_code',
  'invite_code_not_found',
  'survivor_game_not_found',
  'survivor_already_member',
  'survivor_game_full',
  'survivor_game_locked',
  'survivor_limit_reached',
  'survivor_game_not_finished',
  'survivor_already_restarted',
  'survivor_no_rounds_left',
  'team_already_picked',
  'team_not_playing_this_gameweek',
  'team_not_in_competition',
  'pick_locked',
  'participation_eliminated',
  'not_participant',
  'not_game_owner',
  'team_name_taken',
  'no_active_competition',
])

/**
 * @param fallbackKey message for an error with no mapped code — pass something
 *   specific to the screen ('error.survivor.load') rather than the generic one.
 */
export function errorMessage(
  error: unknown,
  t: TranslateFn,
  fallbackKey: TranslationKey = 'error.generic',
): string {
  if (error instanceof ApiError) {
    if (KNOWN_CODES.has(error.errorCode)) {
      return t(`error.${error.errorCode}` as TranslationKey)
    }
    return t(fallbackKey)
  }

  // fetch() rejects with a TypeError when the request never reached the server.
  if (error instanceof TypeError) return t('error.network')

  return t(fallbackKey)
}
